// Essential-only cookie / storage notice. Preferences stored in localStorage.
// Structure leaves room for future analytics opt-in without exposing it at launch.

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export const COOKIE_CONSENT_STORAGE_KEY = 'volugraph_cookie_consent';

const STORAGE_VERSION = 1;

function parseStored(raw) {
  try {
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return null;
    if (p.version === STORAGE_VERSION && typeof p.acknowledgedAt === 'string') {
      return { acknowledged: true, at: p.acknowledgedAt };
    }
    // Legacy: prior banner stored analytics/marketing flags
    if (
      typeof p.consentedAt === 'string' ||
      typeof p.analytics === 'boolean' ||
      typeof p.marketing === 'boolean'
    ) {
      return { acknowledged: true, at: p.consentedAt || null };
    }
    return null;
  } catch {
    return null;
  }
}

function loadStoredAcknowledgement() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return parseStored(raw);
  } catch {
    return null;
  }
}

function persistEssentialAcknowledgement() {
  const payload = {
    version: STORAGE_VERSION,
    essentialOnly: true,
    analytics: false,
    marketing: false,
    acknowledgedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const initial = typeof window !== 'undefined' ? loadStoredAcknowledgement() : null;
  const [hasAcknowledged, setHasAcknowledged] = useState(initial?.acknowledged ?? false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const consent = useMemo(
    () => ({
      essential: true,
      analytics: false,
      marketing: false,
    }),
    [],
  );

  const showBanner = !hasAcknowledged || settingsOpen;

  const acknowledgeEssential = useCallback(() => {
    persistEssentialAcknowledgement();
    setHasAcknowledged(true);
    setSettingsOpen(false);
  }, []);

  /** Reserved for a future consent-gated analytics SDK; no-op at launch. */
  const updateConsent = useCallback(() => {}, []);

  const openCookieSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeCookieSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      hasAcknowledged,
      showBanner,
      acknowledgeEssential,
      updateConsent,
      openCookieSettings,
      closeCookieSettings,
    }),
    [
      consent,
      hasAcknowledged,
      showBanner,
      acknowledgeEssential,
      updateConsent,
      openCookieSettings,
      closeCookieSettings,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
